import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../../auth/contexts/AuthContext";
import { useProjects } from "../../common/contexts/ProjectContext";
import { useProjectNavigation } from "../../../hooks/useProjectNavigation";
import { socketService } from "../../../services/socket";
import { useTags } from "../../common/contexts/TagsContext";
import { RolesEditor } from "../../common/views/RolesEditor";
import { BugReportButton } from "../../common/components/BugReportButton";
import { LanguageToggle } from "../../common/components/LanguageToggle";
import { FullscreenButton } from "../../common/components/FullscreenButton";
import { RoleBrandingHeader } from "../../common/components/RoleBrandingHeader";
import { UserMenu } from "../../common/components/UserMenu";
import { ConnectionStatusWidget } from "../../common/components/ConnectionStatusWidget";
import { ButtonGroup } from "@/components/ui/button-group";
import { BrainEnginePanel } from "../views/BrainEnginePanel";
import { BrainPlayground } from "../views/BrainPlayground";
import { AuditLogs } from "../views/AuditLogs";
import { BrainTestSuite } from "../views/BrainTestSuite";
import { CostCalculator } from "../views/CostCalculator";
import { AIQueueManager } from "../views/AIQueueManager";
import { ProjectCard, ProjectStats } from "../components/ProjectCard";
import { BugsTable } from "../components/BugsTable";
import { LiveTranscriptionsPanel } from "../../logger/components/LiveTranscriptionsPanel";
import { ParticipantsEditor } from "../../controller/views/ParticipantsEditor";
import { TagsEditor } from "../../controller/views/TagsEditor";
import { RundownEditor } from "../../controller/views/RundownEditor";
import { Dashboard } from "../../controller/views/Dashboard";
import { useTimecode } from "../../../hooks/useTimecode";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { StarRating } from "@/components/ui/star-rating";

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

const TOOLTIP_CSS = `
.admin-tip { position: relative; }
.admin-tip::after {
  content: attr(data-tooltip);
  position: absolute; bottom: calc(100% + 8px); left: 50%; transform: translateX(-50%);
  background: #1a1a1a; color: #ccc; font-size: 0.65rem; font-weight: 500;
  padding: 8px 12px; border-radius: 8px; border: 1px solid #444;
  white-space: normal; width: max-content; max-width: 220px; text-align: center;
  box-shadow: 0 8px 24px rgba(0,0,0,0.6); pointer-events: none;
  opacity: 0; transition: opacity 0.2s ease; z-index: 100;
  line-height: 1.4;
}
.admin-tip:hover::after { opacity: 1; }
`;

// Types
// ProjectStats type imported from '../components/ProjectCard'

interface HealthStatus {
  supabase: { status: string; latencyMs: number | null; error: string | null };
  gemini: { status: string; error: string | null };
  timestamp: string;
}

export const AdminLayout: React.FC = () => {
  const { t } = useTranslation();
  const { navigate, projectPath } = useProjectNavigation();
  const { user } = useAuth();
  const { createProject, deleteProject, creationProgress, activeProject } = useProjects();
  const { getTagLabel, getTagColor, getCameraName } = useTags();
  const [projectStats, setProjectStats] = useState<ProjectStats[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showNewProjectModal, setShowNewProjectModal] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  const { currentDisplayTC } = useTimecode();

  // Edit Project state
  const [editingProject, setEditingProject] = useState<ProjectStats | null>(null);
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  // Health check state
  const [health, setHealth] = useState<HealthStatus | null>(null);
  const [healthLoading, setHealthLoading] = useState(false);

  // Delete confirmation state
  const [deleteTarget, setDeleteTarget] = useState<ProjectStats | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");

  // View & Live Feed state — synced with URL search params for deep linking
  const [searchParams, setSearchParams] = useSearchParams();
  const VALID_VIEWS = [
    "dashboard",
    "controldash",
    "logs",
    "roles",
    "audit",
    "bugs",
    "participants",
    "tags",
    "rundowns",
  ] as const;
  type View = (typeof VALID_VIEWS)[number];
  // RundownEditor (shared with Content Director) self-syncs via ?tab=rundowns and clears ?view,
  // so honor that param too — otherwise the missing ?view falls back to the default ("roles").
  const viewParam =
    searchParams.get("view") ?? (searchParams.get("tab") === "rundowns" ? "rundowns" : null);
  const activeView: View = VALID_VIEWS.includes(viewParam as View) ? (viewParam as View) : "roles";
  const VALID_BRAIN_SUBS = ["monitor", "config", "playground", "test-suite", "resumen"] as const;
  type BrainSub = (typeof VALID_BRAIN_SUBS)[number];
  const subParam = searchParams.get("sub");
  const brainSubView: BrainSub = VALID_BRAIN_SUBS.includes(subParam as BrainSub)
    ? (subParam as BrainSub)
    : "monitor";
  const setActiveView = (view: View) => {
    setSearchParams({ view });
  };
  const setBrainSubView = (sub: BrainSub) => {
    setSearchParams(sub === "monitor" ? { view: "brain" } : { view: "brain", sub });
  };
  // Icon + label for each top tab (kept in one place so new tabs are trivial to add)
  const TAB_META: Record<View, { icon: string; label: string; color?: string }> = {
    dashboard: { icon: "bx-grid-alt", label: t("admin.layout.tabs.dashboard") },
    controldash: { icon: "bx-broadcast", label: t("admin.layout.tabs.controlDashboard") },
    logs: { icon: "bx-list-ul", label: t("admin.layout.tabs.liveFeed") },
    roles: { icon: "bx-user-circle", label: t("admin.layout.tabs.roles") },
    audit: { icon: "bx-history", label: t("admin.layout.tabs.audit") },
    bugs: { icon: "bx-bug", label: t("admin.layout.tabs.bugs") },
    participants: { icon: "bx-user", label: t("admin.layout.tabs.participants") },
    tags: { icon: "bx-tag", label: t("admin.layout.tabs.tags") },
    rundowns: {
      icon: "bx-list-ul",
      label: t("admin.layout.tabs.rundowns"),
      color: "var(--color-type-rundown)",
    },
  };
  // Flat top-level tabs vs. tabs grouped under the "Settings" dropdown (as in Content Director)
  const MAIN_VIEWS = ["dashboard", "controldash", "logs", "roles", "audit", "bugs"] as const;
  const SETTINGS_VIEWS = ["participants", "tags", "rundowns"] as const;
  const isSettingsView = (SETTINGS_VIEWS as readonly string[]).includes(activeView);
  const [showSettings, setShowSettings] = useState(false);
  // The tabs bar clips overflow, so the dropdown is portaled to <body> with fixed coords
  const settingsBtnRef = useRef<HTMLButtonElement>(null);
  const [settingsMenuPos, setSettingsMenuPos] = useState<{ top: number; left: number } | null>(
    null,
  );
  const toggleSettings = () => {
    const next = !showSettings;
    if (next && settingsBtnRef.current) {
      const r = settingsBtnRef.current.getBoundingClientRect();
      setSettingsMenuPos({ top: r.bottom + 6, left: r.left + r.width / 2 });
    }
    setShowSettings(next);
  };
  // Brain Engine state
  const [brainStatus, setBrainStatus] = useState<any>(null);
  const [brainLoading, setBrainLoading] = useState(false);
  const [showDailyCostChart, setShowDailyCostChart] = useState(false);
  // Bug Reports state
  const [bugReports, setBugReports] = useState<any[]>([]);
  const [bugsLoading, setBugsLoading] = useState(false);
  // Dashboard state
  const [selectedDashProject, setSelectedDashProject] = useState<string>("");
  const [dashData, setDashData] = useState<any>(null);
  const [dashLoading, setDashLoading] = useState(false);
  // Screenshot preview state
  const [screenshotPreview, setScreenshotPreview] = useState<string | null>(null);
  // Confirm action modal (replaces window.confirm)
  const [confirmAction, setConfirmAction] = useState<{
    title: string;
    message: string;
    onConfirm: () => void;
  } | null>(null);
  const [tooltipInfo, setTooltipInfo] = useState<{ text: string; x: number; y: number } | null>(
    null,
  );

  // Admin Historical Query Logs state
  const [historicalLogs, setHistoricalLogs] = useState<any[]>([]);
  const [logPagination, setLogPagination] = useState({ page: 1, limit: 50, total: 0 });
  const [logSort, setLogSort] = useState({ by: "created_at", order: "desc" });
  const [logFilters, setLogFilters] = useState({ project: "all", mode: "all", user: "all" });
  const [logsLoading, setLogsLoading] = useState(false);
  const [expandedLogId, setExpandedLogId] = useState<string | null>(null);
  const [monitorCitPopup, setMonitorCitPopup] = useState<{
    srcKey: string;
    type: string;
    role: string;
    tc: string | null;
    speaker: string | null;
    text: string;
    x: number;
    y: number;
  } | null>(null);
  const [userList, setUserList] = useState<string[]>([]);

  // Settings for the historical costs chart
  const [costPeriod, setCostPeriod] = useState<string>("30d");
  const [costProjectId, setCostProjectId] = useState<string>("ALL");

  const [staff, setStaff] = useState<any[]>([]);
  const [activeLoggers, setActiveLoggers] = useState<{ id: string; name: string }[]>([]);
  const [feedStories, setFeedStories] = useState<any[]>([]);
  const [feedParticipants, setFeedParticipants] = useState<any[]>([]);
  const [participantRoles, setParticipantRoles] = useState<{ id: string; name: string }[]>([]);
  const [groups, setGroups] = useState<any[]>([]);
  // Content Controller operational dashboard state (rundowns / live counters)
  const [activeRundown, setActiveRundown] = useState<any | null>(null);
  const [rundowns, setRundowns] = useState<any[]>([]);
  const [isConnected, setIsConnected] = useState(socketService.connected);
  const [eventsCount, setEventsCount] = useState(0);
  const [transcriptsCount, setTranscriptsCount] = useState(0);
  const [feedLoading, setFeedLoading] = useState(false);
  const feedEndRef = useRef<HTMLDivElement>(null);
  const [autoScroll, setAutoScroll] = useState(true);

  // Fetch project stats
  const fetchStats = () => {
    socketService.emit("get-project-stats");
  };

  const fetchHealth = () => {
    setHealthLoading(true);
    socketService.emit("admin-health-check");
  };

  const fetchBrainStatus = (period?: string, projId?: string) => {
    setBrainLoading(true);
    const p = typeof period === "string" ? period : costPeriod;
    const id = typeof projId === "string" ? projId : costProjectId;
    socketService.emit("admin-brain-status", { costPeriod: p, costProjectId: id });
  };

  useEffect(() => {
    if ((activeView as string) === "brain" && showDailyCostChart) {
      fetchBrainStatus(costPeriod, costProjectId);
    }
  }, [costPeriod, costProjectId]);

  const fetchBugReports = () => {
    setBugsLoading(true);
    socketService.emit("get-bug-reports");
  };

  const fetchHistoricalLogs = () => {
    setLogsLoading(true);
    socketService.emit("admin-query-logs", {
      limit: logPagination.limit,
      offset: (logPagination.page - 1) * logPagination.limit,
      sortBy: logSort.by,
      sortOrder: logSort.order,
      filterProject: logFilters.project,
      filterMode: logFilters.mode,
      filterUser: logFilters.user,
    });
  };

  useEffect(() => {
    if ((activeView as string) === "brain" && brainSubView === "monitor") {
      fetchHistoricalLogs();
    }
  }, [
    logPagination.page,
    logSort.by,
    logSort.order,
    logFilters.project,
    logFilters.mode,
    logFilters.user,
    activeView,
    brainSubView,
  ]);

  const fetchDashboard = (projectId: string) => {
    if (!projectId) return;
    setDashLoading(true);
    socketService.emit("admin-project-dashboard", { projectId });
  };

  useEffect(() => {
    socketService.connect();

    const handleStats = (data: { projects: ProjectStats[] }) => {
      setProjectStats(data.projects || []);
      setIsLoading(false);
    };

    const handleHealth = (data: HealthStatus) => {
      setHealth(data);
      setHealthLoading(false);
    };

    const handleBrainStatus = (data: any) => {
      setBrainStatus(data);
      setBrainLoading(false);
    };

    const handleBugReports = (data: any[]) => {
      setBugReports(data || []);
      setBugsLoading(false);
    };

    const handleHistoricalLogs = (data: any) => {
      if (data.error) {
        console.error("Error fetching historical logs:", data.error);
      } else {
        setHistoricalLogs(data.queries || []);
        setLogPagination((p) => ({ ...p, total: data.total || 0 }));
        if (data.users) setUserList(data.users);
      }
      setLogsLoading(false);
    };

    const handleDashboard = (data: any) => {
      setDashData(data);
      setDashLoading(false);
    };

    // Live feed handlers
    const handleInitData = (data: any) => {
      if (data.stories) setFeedStories(data.stories);
      if (data.participants) setFeedParticipants(data.participants);
      if (data.roles && Array.isArray(data.roles)) setParticipantRoles(data.roles);
      if (data.groups && Array.isArray(data.groups)) setGroups(data.groups);
      if (data.staff && Array.isArray(data.staff)) setStaff(data.staff);
      if (data.activeLoggers) setActiveLoggers(data.activeLoggers);
      // Content Controller dashboard data
      if (data.rundowns && Array.isArray(data.rundowns)) setRundowns(data.rundowns);
      if (data.activeRundownId && Array.isArray(data.rundowns)) {
        const active = data.rundowns.find((r: any) => r.id === data.activeRundownId);
        if (active) setActiveRundown(active);
      }
      if (data.eventsCount !== undefined) setEventsCount(data.eventsCount);
      if (data.transcriptsCount !== undefined) setTranscriptsCount(data.transcriptsCount);
      setFeedLoading(false);
    };

    const handleStaffUpdate = (updated: any[]) => setStaff(updated);
    const handleLoggersUpdate = (data: { id: string; name: string }[]) => setActiveLoggers(data);

    // Content Controller dashboard — live rundown + connection state
    const handleRundownItemChange = (data: { id: string | null; rundown: any | null }) => {
      if (data.rundown) {
        setActiveRundown({
          id: data.id,
          name: data.rundown.name,
          date: data.rundown.date,
          items: data.rundown.items || [],
        });
      } else {
        setActiveRundown(null);
      }
    };
    const handleRundownUpdate = (data: { id: string; items: any[] }) => {
      setActiveRundown((prev: any) =>
        prev && data.id === prev.id ? { ...prev, items: data.items } : prev,
      );
    };
    const handleConnect = () => setIsConnected(true);
    const handleDisconnect = () => setIsConnected(false);

    // Settings editors (Participants / Tags / Rundowns) — keep data live
    const handleParticipantsUpdate = (updated: any[]) => {
      if (Array.isArray(updated)) setFeedParticipants(updated);
    };
    const handleParticipantRolesUpdate = (updated: { id: string; name: string }[]) => {
      if (Array.isArray(updated)) setParticipantRoles(updated);
    };
    const handleGroupsUpdate = (updated: any[]) => {
      if (Array.isArray(updated)) setGroups(updated);
    };

    const handleLogAdded = (newLog: any) => {
      setFeedStories((prev) =>
        prev.map((story) => {
          if (story.id === newLog.storyId) {
            return { ...story, logs: [...(story.logs || []), newLog] };
          }
          return story;
        }),
      );
    };

    const handleStoriesUpdate = (data: any) => {
      if (Array.isArray(data)) {
        setFeedStories(data);
      } else if (data.stories) {
        setFeedStories(data.stories);
      }
    };

    socketService.on("project-stats", handleStats);
    socketService.on("admin-health-check", handleHealth);
    socketService.on("admin-brain-status", handleBrainStatus);
    socketService.on("bug-reports", handleBugReports);
    socketService.on("admin-project-dashboard", handleDashboard);
    socketService.on("projects-list-updated", () => {
      setTimeout(fetchStats, 500);
    });
    socketService.on("init-data", handleInitData);
    socketService.on("log-added", handleLogAdded);
    socketService.on("stories-updated", handleStoriesUpdate);
    socketService.on("staff-updated", handleStaffUpdate);
    socketService.on("active-loggers-updated", handleLoggersUpdate);
    socketService.on("participants-updated", handleParticipantsUpdate);
    socketService.on("roles-updated", handleParticipantRolesUpdate);
    socketService.on("groups-updated", handleGroupsUpdate);
    socketService.on("active-rundown-changed", handleRundownItemChange);
    socketService.on("rundown-updated", handleRundownUpdate);
    socketService.on("connect", handleConnect);
    socketService.on("disconnect", handleDisconnect);
    socketService.on("admin-query-logs", handleHistoricalLogs);
    // Cost reset response
    const handleCostReset = () => {
      fetchBrainStatus();
      fetchHistoricalLogs();
    };
    socketService.on("admin-query-logs", handleHistoricalLogs);
    socketService.on("admin-cost-reset", handleCostReset);

    const handleProjectUpdateSuccess = () => {
      setIsEditing(false);
      setEditingProject(null);
      setEditError(null);
      fetchStats();
    };

    const handleProjectUpdateError = (data: any) => {
      setIsEditing(false);
      setEditError(data.message);
    };

    socketService.on("project-update-success", handleProjectUpdateSuccess);
    socketService.on("project-update-error", handleProjectUpdateError);

    // Request init data once socket is connected (or immediately if already connected)
    const handleAdminConnect = () => {
      socketService.emit("register-editor");
      socketService.emit("get-init-data", activeProject?.id);
      fetchStats();
      fetchHealth();
    };

    socketService.on("connect", handleAdminConnect);

    if (socketService.connected) {
      handleAdminConnect();
    }

    // Auto-refresh health every 30s
    const healthInterval = setInterval(fetchHealth, 30000);
    // Auto-refresh brain status every 15s when on brain tab
    const brainInterval = setInterval(() => {
      if (document.querySelector('[data-brain-active="true"]')) fetchBrainStatus();
    }, 15000);

    return () => {
      socketService.off("project-stats", handleStats);
      socketService.off("admin-health-check", handleHealth);
      socketService.off("admin-brain-status", handleBrainStatus);
      socketService.off("bug-reports", handleBugReports);
      socketService.off("admin-project-dashboard", handleDashboard);
      socketService.off("init-data", handleInitData);
      socketService.off("log-added", handleLogAdded);
      socketService.off("stories-updated", handleStoriesUpdate);
      socketService.off("staff-updated", handleStaffUpdate);
      socketService.off("active-loggers-updated", handleLoggersUpdate);
      socketService.off("participants-updated", handleParticipantsUpdate);
      socketService.off("roles-updated", handleParticipantRolesUpdate);
      socketService.off("groups-updated", handleGroupsUpdate);
      socketService.off("active-rundown-changed", handleRundownItemChange);
      socketService.off("rundown-updated", handleRundownUpdate);
      socketService.off("connect", handleConnect);
      socketService.off("disconnect", handleDisconnect);
      socketService.off("admin-query-logs", handleHistoricalLogs);
      socketService.off("admin-cost-reset", handleCostReset);
      socketService.off("project-update-success", handleProjectUpdateSuccess);
      socketService.off("project-update-error", handleProjectUpdateError);
      socketService.off("connect", handleAdminConnect);
      clearInterval(healthInterval);
      clearInterval(brainInterval);
    };
  }, []);

  // Auto-close modal when creation is ready
  useEffect(() => {
    if (creationProgress.status === "ready") {
      setShowNewProjectModal(false);
      setName("");
      setDescription("");
      setTimeout(fetchStats, 1000);
    }
  }, [creationProgress.status]);

  const handleCreateProject = (e: React.FormEvent) => {
    e.preventDefault();
    if (name.trim() && creationProgress.status === "idle") {
      createProject(name, description);
    }
  };

  const handleEditSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingProject) return;
    setIsEditing(true);
    setEditError(null);
    socketService.emit("update-project", {
      id: editingProject.id,
      name: editName,
      description: editDescription,
    });
  };

  const handleDeleteClick = (proj: ProjectStats) => {
    setDeleteTarget(proj);
    setDeleteConfirmText("");
  };

  const executeDelete = () => {
    if (deleteTarget && deleteConfirmText === "DELETE") {
      deleteProject(deleteTarget.id);
      setDeleteTarget(null);
      setDeleteConfirmText("");
      setTimeout(fetchStats, 1000);
    }
  };

  // Calculate global stats
  const globalStats = React.useMemo(() => {
    let rundowns = 0,
      stories = 0,
      logs = 0,
      transcripts = 0;
    projectStats.forEach((p) => {
      rundowns += p?.counts?.rundowns || 0;
      stories += p?.counts?.stories || 0;
      logs += p?.counts?.logs || 0;
      transcripts += p?.counts?.transcripts || 0;
    });
    return {
      projects: projectStats.length,
      rundowns,
      stories,
      logs,
      transcripts,
      activeUsers: activeLoggers.length,
    };
  }, [projectStats, activeLoggers]);

  // Format large numbers
  const fmt = (n: any) => {
    const val = Number(n || 0);
    return val >= 1000 ? `${(val / 1000).toFixed(1)}k` : String(val);
  };

  // Metric Component (Widget Style)
  const Metric = ({
    label,
    value,
    icon,
    color = "#888",
  }: {
    label: string;
    value: number | string;
    icon: string;
    color?: string;
  }) => (
    <div
      style={{ display: "flex", flexDirection: "column", alignItems: "center", margin: "0 6px" }}
      title={label}
    >
      <div style={{ fontSize: "0.85rem", fontWeight: "900", color: "#fff", lineHeight: 1 }}>
        {value}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: "4px", marginTop: "2px" }}>
        <i className={`bx ${icon}`} style={{ fontSize: "0.65rem", color }}></i>
      </div>
    </div>
  );

  // Live feed: global logs sorted by creation time (newest first)
  const globalLogs = feedStories
    .flatMap((s: any) => (s.logs || []).map((l: any) => ({ ...l, author: s.author })))
    .sort((a: any, b: any) => (b.createdAt || 0) - (a.createdAt || 0));

  // Request feed data when switching to logs view
  const loadFeed = () => {
    setFeedLoading(true);
    if (activeProject?.id) {
      socketService.emit("select-project", activeProject.id);
    } else {
      setFeedLoading(false);
    }
  };

  // Lazy-load logs for a story when expanded on the Content Controller dashboard
  const handleFetchStoryLogs = (storyId: string, filters?: any) => {
    socketService.emit("get-story-logs", { storyId, filters }, (logs: any[]) => {
      if (logs && logs.length > 0) {
        setFeedStories((prev) => prev.map((s) => (s.id === storyId ? { ...s, logs } : s)));
      }
    });
  };

  // Auto-scroll feed
  useEffect(() => {
    if (autoScroll && feedEndRef.current && activeView === "logs") {
      feedEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [globalLogs.length, activeView, autoScroll]);

  const ADMIN_COLOR = "#A3E635";

  // Health status helper
  const getStatusDot = (status: string) => {
    const colors: Record<string, string> = {
      connected: "#10B981",
      configured: "#10B981",
      not_configured: "#F59E0B",
      no_api_key: "#F59E0B",
      error: "#EF4444",
      unknown: "#666",
    };
    const color = colors[status] || "#666";
    return (
      <div
        style={{
          width: "8px",
          height: "8px",
          borderRadius: "50%",
          background: color,
          boxShadow:
            status === "connected" || status === "configured" ? `0 0 8px ${color}` : "none",
          flexShrink: 0,
        }}
      ></div>
    );
  };

  return (
    <div
      data-testid="admin-layout"
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        background: "var(--color-bg-mesh)",
        color: "var(--color-text-primary)",
        fontFamily: "var(--font-family)",
        overflow: "hidden",
      }}
    >
      {/* Tooltip CSS — static constant injected safely */}
      <style>{TOOLTIP_CSS}</style>
      {/* ====== TOP HEADER (Thematic Style) ====== */}
      <header
        className="no-print"
        style={{
          height: "88px",
          boxSizing: "border-box",
          padding: "0 min(40px, 3vw)",
          borderBottom: `5px solid ${ADMIN_COLOR}`,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          background: "var(--color-panel-bg)",
          backdropFilter: "blur(10px)",
          boxShadow: `0 0 20px ${ADMIN_COLOR}15`,
          zIndex: 100,
          flexShrink: 0,
          position: "relative",
        }}
      >
        {/* LEFT: Logo & Title & Apps Menu */}
        <div style={{ display: "flex", alignItems: "center", gap: "20px", flex: 1 }}>
          <RoleBrandingHeader
            role="admin"
            roleLabel={t("admin.layout.admin").toUpperCase()}
            onHomeClick={() => navigate("/projects")}
            testIdPrefix="admin"
          />
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}></div>
        </div>

        {/* CENTER: Tabs Navigation (Moved from Sub-Header) */}
        <div
          className="admin-tabs-scroll"
          style={{
            flex: 3,
            display: "flex",
            justifyContent: "center",
            overflowX: "auto",
            padding: "0 10px",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: "8px",
              background: "rgba(255,255,255,0.03)",
              borderRadius: "12px",
              padding: "6px",
              border: "1px solid rgba(255,255,255,0.05)",
              minWidth: "max-content",
            }}
          >
            {MAIN_VIEWS.map((view) => (
              <button
                key={view}
                data-testid={`admin-tab-${view}`}
                onClick={() => {
                  setActiveView(view);
                  if (view === "logs" && feedStories.length === 0) loadFeed();
                  if (view === "controldash" && rundowns.length === 0 && feedStories.length === 0)
                    loadFeed();
                  if ((view as string) === "brain" && !brainStatus) fetchBrainStatus();
                  if (view === "bugs" && bugReports.length === 0) fetchBugReports();
                  if (view === "dashboard" && !dashData && projectStats.length > 0) {
                    const firstProjId = projectStats[0]?.id;
                    if (firstProjId && !selectedDashProject) {
                      setSelectedDashProject(firstProjId);
                      fetchDashboard(firstProjId);
                    }
                  }
                }}
                style={{
                  background: activeView === view ? ADMIN_COLOR : "transparent",
                  color: activeView === view ? "#fff" : "#888",
                  border: "none",
                  padding: "8px 16px",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "0.8rem",
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  transition: "all 0.2s",
                  whiteSpace: "nowrap",
                }}
              >
                <i className={`bx ${TAB_META[view].icon}`} style={{ fontSize: "1.1rem" }}></i>{" "}
                {TAB_META[view].label}
              </button>
            ))}

            {/* SETTINGS (Dropdown) — Participants, Tags, Rundowns (from Content Director) */}
            <div data-testid="admin-settings-dropdown" style={{ position: "relative" }}>
              <button
                ref={settingsBtnRef}
                data-testid="admin-tab-settings"
                onClick={toggleSettings}
                onBlur={() => setTimeout(() => setShowSettings(false), 200)}
                style={{
                  background: isSettingsView ? ADMIN_COLOR : "transparent",
                  color: isSettingsView ? "#fff" : "#888",
                  border: "none",
                  padding: "8px 16px",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "0.8rem",
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  transition: "all 0.2s",
                  whiteSpace: "nowrap",
                }}
              >
                <i className="bx bx-cog" style={{ fontSize: "1.1rem" }}></i>{" "}
                {t("admin.layout.tabs.settings")}{" "}
                <i className={`bx bx-chevron-${showSettings ? "up" : "down"}`}></i>
              </button>

              {showSettings &&
                settingsMenuPos &&
                createPortal(
                  <div
                    style={{
                      position: "fixed",
                      top: settingsMenuPos.top,
                      left: settingsMenuPos.left,
                      transform: "translateX(-50%)",
                      width: "min(200px, 45vw)",
                      background: "var(--color-panel-bg)",
                      border: "1px solid var(--color-panel-border)",
                      borderRadius: "8px",
                      padding: "8px",
                      display: "flex",
                      flexDirection: "column",
                      gap: "4px",
                      boxShadow: "0 10px 25px rgba(0,0,0,0.5)",
                      zIndex: 1000,
                    }}
                  >
                    {SETTINGS_VIEWS.map((view) => (
                      <button
                        key={view}
                        data-testid={`admin-settings-${view}`}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          setActiveView(view);
                          setShowSettings(false);
                        }}
                        style={{
                          textAlign: "left",
                          padding: "10px 12px",
                          background:
                            activeView === view ? "var(--color-selection-bg)" : "transparent",
                          color: activeView === view ? TAB_META[view].color || ADMIN_COLOR : "#ccc",
                          border: "none",
                          borderLeft:
                            activeView === view && TAB_META[view].color
                              ? `3px solid ${TAB_META[view].color}`
                              : "3px solid transparent",
                          borderRadius: "6px",
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.background = "rgba(255,255,255,0.05)")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.background =
                            activeView === view ? "var(--color-selection-bg)" : "transparent")
                        }
                      >
                        <i
                          className={`bx ${TAB_META[view].icon}`}
                          style={{ fontSize: "1rem", color: TAB_META[view].color || "inherit" }}
                        ></i>
                        {TAB_META[view].label}
                      </button>
                    ))}
                  </div>,
                  document.body,
                )}
            </div>
          </div>
        </div>

        {/* RIGHT: Health & User Profile */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            justifyContent: "flex-end",
            flex: 1,
          }}
        >
          <ButtonGroup>
            <LanguageToggle />
            <BugReportButton module="admin" />
            <FullscreenButton isInline={true} />
          </ButtonGroup>

          <ConnectionStatusWidget />

          <UserMenu role="admin" testIdPrefix="admin-user" />
        </div>
      </header>

      {/* Main Content Area */}
      <div style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
        {/* Sub-Header / Tool Bar */}
        <div
          className="no-print"
          style={{
            background: "rgba(0, 0, 0, 0.4)",
            borderBottom: "1px solid var(--color-panel-border)",
            padding: "15px 25px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexShrink: 0,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "20px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
              <i
                className="bx bx-shield-quarter"
                style={{ fontSize: "1.4rem", color: ADMIN_COLOR }}
              ></i>
              <div>
                <div
                  style={{
                    fontSize: "1.1rem",
                    fontWeight: "900",
                    letterSpacing: "2px",
                    color: ADMIN_COLOR,
                  }}
                >
                  {activeView === "dashboard"
                    ? t("admin.layout.projectDashboard")
                    : activeView === "logs"
                      ? t("admin.layout.liveFeed")
                      : activeView === "roles"
                        ? t("admin.layout.rolesPermissions")
                        : activeView === "audit"
                          ? "Audit log"
                          : t("admin.layout.bugReports")}
                </div>
                <div
                  style={{ fontSize: "0.7rem", color: "var(--color-text-muted)", marginTop: "2px" }}
                >
                  {activeView === "dashboard"
                    ? t("admin.layout.projectDashboardDesc")
                    : activeView === "logs"
                      ? t("admin.layout.liveFeedDesc")
                      : activeView === "roles"
                        ? t("admin.layout.rolesPermissionsDesc")
                        : activeView === "audit"
                          ? "Historial de cambios de rol y asignación de proyecto"
                          : t("admin.layout.bugReportsDesc")}
                </div>
              </div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
            {/* Global Stats & System Health Widget */}
            <div
              style={{
                display: "flex",
                gap: "4px",
                alignItems: "center",
                background: "rgba(255,255,255,0.03)",
                padding: "4px 12px",
                borderRadius: "12px",
                border: "1px solid rgba(255,255,255,0.05)",
              }}
            >
              {/* System Health */}
              <div
                style={{
                  display: "flex",
                  gap: "6px",
                  alignItems: "center",
                  marginRight: "6px",
                  paddingRight: "10px",
                  borderRight: "1px solid rgba(255,255,255,0.1)",
                }}
              >
                {health ? (
                  <>
                    <div title={t("admin.layout.supabaseDb")}>
                      {getStatusDot(health.supabase.status)}
                    </div>
                    <div title={t("admin.layout.geminiAI")}>
                      {getStatusDot(health.gemini.status)}
                    </div>
                    <button
                      onClick={fetchHealth}
                      style={{
                        background: "transparent",
                        border: "none",
                        color: "var(--color-text-muted)",
                        cursor: "pointer",
                        fontSize: "0.7rem",
                        padding: "0 4px",
                        opacity: healthLoading ? 0.4 : 0.7,
                        transition: "opacity 0.2s",
                      }}
                    >
                      <i className="bx bx-refresh"></i>
                    </button>
                  </>
                ) : null}
              </div>

              {/* Global Data Metrics */}
              <Metric
                label={t("admin.layout.projects")}
                value={globalStats.projects}
                icon="bx-briefcase"
                color={ADMIN_COLOR}
              />
              <div
                style={{
                  width: "1px",
                  height: "16px",
                  background: "rgba(255,255,255,0.1)",
                  margin: "0 2px",
                }}
              ></div>
              <Metric
                label={t("admin.layout.rundowns")}
                value={globalStats.rundowns}
                icon="bx-list-ul"
                color="var(--color-data-rundown, #F59E0B)"
              />
              <div
                style={{
                  width: "1px",
                  height: "16px",
                  background: "rgba(255,255,255,0.1)",
                  margin: "0 2px",
                }}
              ></div>
              <Metric
                label={t("admin.layout.stories")}
                value={globalStats.stories}
                icon="bx-purchase-tag"
                color="var(--color-data-story, #10B981)"
              />
              <div
                style={{
                  width: "1px",
                  height: "16px",
                  background: "rgba(255,255,255,0.1)",
                  margin: "0 2px",
                }}
              ></div>
              <Metric
                label={t("admin.layout.logs")}
                value={fmt(globalStats.logs)}
                icon="bx-detail"
                color="var(--color-data-log, #F43F5E)"
              />
              <div
                style={{
                  width: "1px",
                  height: "16px",
                  background: "rgba(255,255,255,0.1)",
                  margin: "0 2px",
                }}
              ></div>
              <Metric
                label={t("admin.layout.transcripts")}
                value={fmt(globalStats.transcripts)}
                icon="bx-microphone"
                color="var(--color-data-transcript, #A855F7)"
              />
            </div>
            <div style={{ fontSize: "0.7rem", color: "var(--color-text-muted)" }}>
              {new Date().toLocaleDateString("en-US", {
                weekday: "long",
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div
          data-testid="admin-content-area"
          style={{ flex: 1, overflowY: "auto", padding: "25px" }}
        >
          {activeView === "dashboard" ? (
            /* ====== DASHBOARD VIEW ====== */
            <div
              data-testid="admin-view-dashboard"
              style={{ display: "flex", flexDirection: "column", gap: "20px" }}
            >
              {/* Project Selector — Premium Cards */}
              <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
                {projectStats.map((p) => {
                  const isSelected = selectedDashProject === p.id;
                  return (
                    <button
                      key={p.id}
                      data-testid={`admin-dashboard-project-${p.id}`}
                      onClick={() => {
                        setSelectedDashProject(p.id);
                        fetchDashboard(p.id);
                      }}
                      style={{
                        background: isSelected
                          ? `linear-gradient(135deg, rgba(163, 230, 53, 0.2), rgba(163, 230, 53, 0.05))`
                          : "var(--color-panel-bg)",
                        border: isSelected
                          ? `2px solid ${ADMIN_COLOR}`
                          : "2px solid var(--color-panel-border)",
                        borderRadius: "12px",
                        padding: "14px 20px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        transition: "all 0.25s ease",
                        minWidth: "min(200px, 40vw)",
                        boxShadow: isSelected ? `0 4px 20px rgba(163, 230, 53, 0.2)` : "none",
                      }}
                      onMouseEnter={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.borderColor = "rgba(163, 230, 53, 0.4)";
                          e.currentTarget.style.background = "rgba(163, 230, 53, 0.05)";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.borderColor = "var(--color-panel-border)";
                          e.currentTarget.style.background = "var(--color-panel-bg)";
                        }
                      }}
                    >
                      <div
                        style={{
                          width: "36px",
                          height: "36px",
                          borderRadius: "10px",
                          background: isSelected ? ADMIN_COLOR : "rgba(255,255,255,0.05)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          transition: "all 0.25s",
                        }}
                      >
                        <i
                          className="bx bx-briefcase-alt-2"
                          style={{
                            fontSize: "1.1rem",
                            color: isSelected ? "#fff" : "#666",
                          }}
                        ></i>
                      </div>
                      <div style={{ textAlign: "left" }}>
                        <div
                          style={{
                            fontSize: "0.85rem",
                            fontWeight: 800,
                            color: isSelected ? "#fff" : "#888",
                            letterSpacing: "0.3px",
                          }}
                        >
                          {p.name}
                        </div>
                        <div
                          style={{
                            fontSize: "0.55rem",
                            fontWeight: 600,
                            color: isSelected ? ADMIN_COLOR : "#555",
                            marginTop: "2px",
                          }}
                        >
                          {p.counts?.rundowns || 0} rundowns · {p.counts?.events || 0} events
                        </div>
                      </div>
                      {isSelected && (
                        <i
                          className={`bx bx-refresh ${dashLoading ? "bx-spin" : ""}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            fetchDashboard(p.id);
                          }}
                          style={{
                            marginLeft: "auto",
                            fontSize: "1rem",
                            color: ADMIN_COLOR,
                            cursor: "pointer",
                            padding: "4px",
                          }}
                        ></i>
                      )}
                    </button>
                  );
                })}
              </div>

              {dashLoading && !dashData ? (
                <div style={{ display: "flex", justifyContent: "center", padding: "60px" }}>
                  <div className="loader"></div>
                </div>
              ) : dashData ? (
                <>
                  {/* Content Counts Grid */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                      gap: "12px",
                    }}
                  >
                    {[
                      {
                        label: t("admin.layout.rundowns"),
                        value: dashData.counts?.rundowns || 0,
                        icon: "bx-calendar",
                        color: "var(--color-data-rundown, #D4A842)",
                        info: t("admin.layout.rundownsDesc"),
                      },
                      {
                        label: t("admin.layout.eventsLabel"),
                        value: dashData.counts?.events || 0,
                        icon: "bx-flag",
                        color: "var(--color-data-event, #3B82F6)",
                        info: t("admin.layout.eventsDesc"),
                      },
                      {
                        label: t("admin.layout.storiesLabel"),
                        value: dashData.counts?.stories || 0,
                        icon: "bx-book-open",
                        color: "var(--color-data-story, #10B981)",
                        info: t("admin.layout.storiesDesc"),
                      },
                      {
                        label: t("admin.layout.logsLabel"),
                        value: dashData.counts?.logs || 0,
                        icon: "bx-note",
                        color: "var(--color-data-log, #F72D60)",
                        info: t("admin.layout.logsDesc"),
                      },
                      {
                        label: t("admin.layout.transcriptsLabel"),
                        value: dashData.counts?.transcripts || 0,
                        icon: "bx-microphone",
                        color: "var(--color-data-transcript, #A855F7)",
                        info: t("admin.layout.transcriptsDesc"),
                      },
                      {
                        label: t("admin.layout.tagsLabel"),
                        value: dashData.counts?.tags || 0,
                        icon: "bx-purchase-tag",
                        color: "#F59E0B",
                        info: t("admin.layout.tagsDesc"),
                      },
                    ].map((item) => (
                      <div
                        key={item.label}
                        className="admin-tip"
                        data-tooltip={item.info}
                        style={{
                          background: "var(--color-panel-bg)",
                          border: "1px solid var(--color-panel-border)",
                          borderRadius: "12px",
                          padding: "16px",
                          textAlign: "center",
                          cursor: "help",
                        }}
                      >
                        <i
                          className={`bx ${item.icon}`}
                          style={{
                            fontSize: "1.4rem",
                            color: item.color,
                            marginBottom: "6px",
                            display: "block",
                          }}
                        ></i>
                        <div style={{ fontSize: "1.5rem", fontWeight: 900, color: "#fff" }}>
                          {item.value}
                        </div>
                        <div
                          style={{
                            fontSize: "0.6rem",
                            fontWeight: 800,
                            color: "var(--color-text-muted)",
                            letterSpacing: "0.5px",
                            marginTop: "4px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "4px",
                          }}
                        >
                          {item.label}
                          <i
                            className="bx bx-info-circle"
                            style={{ fontSize: "0.65rem", color: "rgba(255,255,255,0.15)" }}
                          ></i>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Unified Team Panel */}
                  <div
                    style={{
                      background: "var(--color-panel-bg)",
                      border: "1px solid var(--color-panel-border)",
                      borderRadius: "12px",
                      padding: "20px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        marginBottom: "16px",
                      }}
                    >
                      <i
                        className="bx bx-group"
                        style={{ fontSize: "1.1rem", color: ADMIN_COLOR }}
                      ></i>
                      <span
                        style={{
                          fontSize: "0.75rem",
                          fontWeight: 800,
                          color: ADMIN_COLOR,
                          letterSpacing: "1px",
                        }}
                      >
                        TEAM ({(dashData.staff || []).length})
                      </span>
                      {(dashData.connectedUsers || []).length > 0 && (
                        <span
                          style={{
                            fontSize: "0.6rem",
                            fontWeight: 800,
                            padding: "3px 10px",
                            borderRadius: "10px",
                            background: "rgba(16, 185, 129, 0.12)",
                            color: "#10B981",
                            display: "flex",
                            alignItems: "center",
                            gap: "5px",
                          }}
                        >
                          <span
                            style={{
                              width: "6px",
                              height: "6px",
                              borderRadius: "50%",
                              background: "#10B981",
                              boxShadow: "0 0 6px #10B981",
                              display: "inline-block",
                            }}
                          ></span>
                          {(dashData.connectedUsers || []).length} LIVE
                        </span>
                      )}
                    </div>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
                        gap: "8px",
                      }}
                    >
                      {(dashData.staff || []).length === 0 ? (
                        <div
                          style={{
                            color: "var(--color-text-muted)",
                            fontStyle: "italic",
                            fontSize: "0.8rem",
                            gridColumn: "1/-1",
                          }}
                        >
                          No staff assigned
                        </div>
                      ) : (
                        (dashData.staff || []).map((s: any, i: number) => {
                          const isLive = (dashData.connectedUsers || []).some(
                            (u: any) => u.name.toUpperCase() === (s.name || "").toUpperCase(),
                          );
                          const roleColors: any = {
                            showrunner: {
                              bg: "rgba(249, 115, 22, 0.12)",
                              text: "var(--color-role-showrunner)",
                            },
                            admin: { bg: "rgba(163, 230, 53, 0.12)", text: ADMIN_COLOR },
                            storyproducer: { bg: "rgba(16, 185, 129, 0.12)", text: "#10B981" },
                            contentdirector: {
                              bg: "rgba(139, 92, 246, 0.12)",
                              text: "var(--color-role-contentdirector)",
                            },
                            logger: { bg: "rgba(247, 45, 96, 0.12)", text: "#F72D60" },
                          };
                          const roleKey = (s.app_role || s.role || "logger")
                            .toLowerCase()
                            .replace(/\s+/g, "");
                          const rc =
                            roleColors[roleKey] || roleColors["storyproducer"] || roleColors.logger;
                          return (
                            <div
                              key={i}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "10px",
                                padding: "10px 14px",
                                background: isLive
                                  ? "rgba(16, 185, 129, 0.04)"
                                  : "rgba(255,255,255,0.015)",
                                borderRadius: "10px",
                                border: isLive
                                  ? "1px solid rgba(16, 185, 129, 0.2)"
                                  : "1px solid rgba(255,255,255,0.04)",
                                transition: "all 0.2s",
                              }}
                            >
                              {/* Live indicator */}
                              <div
                                style={{
                                  width: "10px",
                                  height: "10px",
                                  borderRadius: "50%",
                                  background: isLive ? "#10B981" : "#333",
                                  boxShadow: isLive ? "0 0 10px rgba(16, 185, 129, 0.6)" : "none",
                                  border: isLive ? "none" : "2px solid #444",
                                  flexShrink: 0,
                                  transition: "all 0.3s",
                                }}
                              />
                              {/* Name */}
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div
                                  style={{
                                    fontSize: "0.8rem",
                                    fontWeight: 700,
                                    color: isLive ? "#fff" : "#666",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {s.name}
                                </div>
                              </div>
                              {/* Role badge */}
                              <span
                                style={{
                                  fontSize: "0.5rem",
                                  fontWeight: 800,
                                  padding: "3px 8px",
                                  borderRadius: "6px",
                                  background: rc.bg,
                                  color: rc.text,
                                  textTransform: "uppercase" as const,
                                  letterSpacing: "0.5px",
                                  flexShrink: 0,
                                }}
                              >
                                {s.app_role || s.role || "logger"}
                              </span>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>

                  {/* Brain Stats for this project */}
                  <div
                    style={{
                      background: "var(--color-panel-bg)",
                      border: "1px solid var(--color-panel-border)",
                      borderRadius: "12px",
                      padding: "20px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "0.7rem",
                        fontWeight: 800,
                        color: ADMIN_COLOR,
                        letterSpacing: "1px",
                        marginBottom: "16px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <i className="bx bx-brain" style={{ fontSize: "1rem" }}></i>
                      {t("admin.layout.brainEngineProject")}
                    </div>

                    {/* System Health Cards */}
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
                        gap: "10px",
                        marginBottom: "16px",
                      }}
                    >
                      {[
                        {
                          label: t("admin.layout.aiQueue"),
                          value: `${brainStatus?.aiQueue?.active || 0}/${brainStatus?.aiQueue?.max || 5}`,
                          color: "#10B981",
                          icon: "bx-loader-circle",
                          info: t("admin.layout.aiQueueDesc"),
                        },
                        {
                          label: t("admin.layout.circuit"),
                          value: brainStatus?.circuitBreaker?.open ? "OPEN" : "OK",
                          color: brainStatus?.circuitBreaker?.open ? "#EF4444" : "#10B981",
                          icon: "bx-shield-alt-2",
                          info: t("admin.layout.circuitDesc"),
                        },
                        {
                          label: t("admin.layout.uptime"),
                          value: brainStatus?.uptime
                            ? (() => {
                                const h = Math.floor(brainStatus.uptime / 3600);
                                const m = Math.floor((brainStatus.uptime % 3600) / 60);
                                return h > 0 ? `${h}h ${m}m` : `${m}m`;
                              })()
                            : "—",
                          color: "#6366F1",
                          icon: "bx-time-five",
                          info: t("admin.layout.uptimeDesc"),
                        },
                        {
                          label: t("admin.layout.streams"),
                          value: brainStatus?.activeStreams || 0,
                          color: "#F59E0B",
                          icon: "bx-transfer-alt",
                          info: t("admin.layout.streamsDesc"),
                        },
                        {
                          label: t("admin.layout.limits"),
                          value: `${brainStatus?.rateLimits?.perMinute || 3}/min`,
                          color: "#F43F5E",
                          icon: "bx-tachometer",
                          info: t("admin.layout.limitsDesc"),
                        },
                      ].map((item) => (
                        <div
                          key={item.label}
                          className="admin-tip"
                          data-tooltip={item.info}
                          style={{
                            background: "rgba(0,0,0,0.2)",
                            borderRadius: "8px",
                            padding: "10px",
                            textAlign: "center",
                            border: "1px solid rgba(255,255,255,0.05)",
                            cursor: "help",
                          }}
                        >
                          <i
                            className={`bx ${item.icon}`}
                            style={{
                              fontSize: "1rem",
                              color: item.color,
                              display: "block",
                              marginBottom: "4px",
                            }}
                          ></i>
                          <div style={{ fontSize: "1rem", fontWeight: 900, color: item.color }}>
                            {item.value}
                          </div>
                          <div
                            style={{
                              fontSize: "0.45rem",
                              fontWeight: 800,
                              color: "var(--color-text-muted)",
                              letterSpacing: "0.5px",
                              marginTop: "2px",
                            }}
                          >
                            {item.label}
                          </div>
                        </div>
                      ))}
                    </div>

                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(4, 1fr)",
                        gap: "12px",
                        marginBottom: "16px",
                      }}
                    >
                      {[
                        {
                          label: t("admin.layout.aiSessions"),
                          value: dashData.brain?.chatSessions || 0,
                          color: ADMIN_COLOR,
                          info: t("admin.layout.aiSessionsDesc"),
                        },
                        {
                          label: t("admin.layout.totalQueries"),
                          value: dashData.brain?.totalQueries || 0,
                          color: "#3B82F6",
                          info: t("admin.layout.totalQueriesDesc"),
                        },
                        {
                          label: t("admin.layout.totalCost"),
                          value: `$${(dashData.brain?.totalCost || 0).toFixed(4)}`,
                          color: "#10B981",
                          info: t("admin.layout.totalCostDesc"),
                        },
                        {
                          label: t("admin.layout.queriesToday"),
                          value: (dashData.brain?.todayLogs || []).length,
                          color: "#F59E0B",
                          info: t("admin.layout.queriesTodayDesc"),
                        },
                      ].map((item) => (
                        <div
                          key={item.label}
                          className="admin-tip"
                          data-tooltip={item.info}
                          style={{
                            background: "rgba(0,0,0,0.2)",
                            borderRadius: "8px",
                            padding: "12px",
                            textAlign: "center",
                            border: "1px solid rgba(255,255,255,0.05)",
                            cursor: "help",
                          }}
                        >
                          <div style={{ fontSize: "1.3rem", fontWeight: 900, color: item.color }}>
                            {item.value}
                          </div>
                          <div
                            style={{
                              fontSize: "0.55rem",
                              fontWeight: 800,
                              color: "var(--color-text-muted)",
                              letterSpacing: "0.5px",
                              marginTop: "4px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: "3px",
                            }}
                          >
                            {item.label}
                            <i
                              className="bx bx-info-circle"
                              style={{ fontSize: "0.55rem", color: "rgba(255,255,255,0.15)" }}
                            ></i>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Cost Reset Info + Button */}
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "16px",
                      }}
                    >
                      <div>
                        {dashData.brain?.costResetSince && (
                          <span style={{ fontSize: "0.55rem", color: "#888" }}>
                            {t("admin.layout.dataSince", {
                              date: new Date(dashData.brain.costResetSince).toLocaleDateString(
                                "en-US",
                                { day: "numeric", month: "short", year: "numeric" },
                              ),
                            })}
                          </span>
                        )}
                      </div>
                      <button
                        onClick={() => {
                          setConfirmAction({
                            title: t("admin.layout.resetStats"),
                            message: t("admin.layout.resetStatsMessage"),
                            onConfirm: () => {
                              socketService.emit("admin-reset-cost", {
                                projectId: selectedDashProject,
                              });
                              setConfirmAction(null);
                              // Refresh dashboard after short delay
                              setTimeout(() => fetchDashboard(selectedDashProject), 1000);
                            },
                          });
                        }}
                        style={{
                          background: "rgba(239, 68, 68, 0.08)",
                          border: "1px solid rgba(239, 68, 68, 0.25)",
                          color: "#EF4444",
                          padding: "5px 12px",
                          borderRadius: "6px",
                          cursor: "pointer",
                          fontSize: "0.6rem",
                          fontWeight: 800,
                          display: "flex",
                          alignItems: "center",
                          gap: "5px",
                        }}
                      >
                        <i className="bx bx-reset"></i> {t("admin.layout.resetCost")}
                      </button>
                    </div>

                    {/* Today's queries table */}
                    {(dashData.brain?.todayLogs || []).length > 0 && (
                      <div style={{ overflowX: "auto" }}>
                        <div
                          style={{
                            fontSize: "0.6rem",
                            fontWeight: 800,
                            color: "var(--color-text-muted)",
                            marginBottom: "8px",
                          }}
                        >
                          {t("admin.layout.todaysQueries")}
                        </div>
                        <table
                          style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.7rem" }}
                        >
                          <thead>
                            <tr style={{ borderBottom: "1px solid var(--color-panel-border)" }}>
                              {[
                                t("admin.layout.tableUser"),
                                t("admin.layout.tableQuery"),
                                t("admin.layout.tableModel"),
                                t("admin.layout.tableTokens"),
                                t("admin.layout.tableCost"),
                                t("admin.layout.tableTime"),
                              ].map((h) => (
                                <th
                                  key={h}
                                  style={{
                                    padding: "6px 8px",
                                    textAlign: "left",
                                    fontSize: "0.55rem",
                                    fontWeight: 800,
                                    color: "var(--color-text-muted)",
                                  }}
                                >
                                  {h}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {(dashData.brain?.todayLogs || [])
                              .slice(0, 10)
                              .map((log: any, i: number) => (
                                <tr
                                  key={i}
                                  style={{ borderBottom: "1px solid rgba(255,255,255,0.03)" }}
                                >
                                  <td
                                    style={{ padding: "6px 8px", color: "#fff", fontWeight: 700 }}
                                  >
                                    {log.user_email?.split("@")[0] || "—"}
                                  </td>
                                  <td
                                    style={{
                                      padding: "6px 8px",
                                      maxWidth: "200px",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      color: "#ccc",
                                    }}
                                  >
                                    {log.query}
                                  </td>
                                  <td
                                    style={{
                                      padding: "6px 8px",
                                      color: "var(--color-text-muted)",
                                      fontFamily: "var(--font-mono)",
                                    }}
                                  >
                                    {(log.model || "").split("-").pop()}
                                  </td>
                                  <td style={{ padding: "6px 8px", color: "#F59E0B" }}>
                                    {(
                                      (log.input_tokens || 0) + (log.output_tokens || 0)
                                    ).toLocaleString()}
                                  </td>
                                  <td
                                    style={{
                                      padding: "6px 8px",
                                      color: "#10B981",
                                      fontFamily: "var(--font-mono)",
                                    }}
                                  >
                                    ${(log.cost || 0).toFixed(4)}
                                  </td>
                                  <td
                                    style={{ padding: "6px 8px", color: "var(--color-text-muted)" }}
                                  >
                                    {new Date(log.created_at).toLocaleTimeString("es-ES", {
                                      hour: "2-digit",
                                      minute: "2-digit",
                                    })}
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "300px",
                    color: "var(--color-text-muted)",
                    fontSize: "0.9rem",
                  }}
                >
                  <div style={{ textAlign: "center" }}>
                    <i
                      className="bx bx-grid-alt"
                      style={{
                        fontSize: "3rem",
                        display: "block",
                        marginBottom: "12px",
                        color: ADMIN_COLOR,
                      }}
                    ></i>
                    Select a project to view its dashboard
                  </div>
                </div>
              )}
            </div>
          ) : activeView === "logs" ? (
            /* ====== LIVE FEED & STREAM VIEW ====== */
            <div
              data-testid="admin-view-logs-container"
              style={{ display: "flex", gap: "20px", height: "100%", overflow: "hidden" }}
            >
              <div
                data-testid="admin-view-logs"
                style={{
                  background: "var(--color-panel-bg)",
                  border: "1px solid var(--color-panel-border)",
                  borderRadius: "16px",
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                  flex: 1,
                  overflow: "hidden",
                }}
              >
                {/* Feed Header */}
                <div
                  style={{
                    padding: "15px 20px",
                    borderBottom: "1px solid var(--color-panel-border)",
                    background: "var(--color-bg-base)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexShrink: 0,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <i
                      className="bx bx-broadcast"
                      style={{ fontSize: "1.2rem", color: "var(--color-accent-primary)" }}
                    ></i>
                    <span
                      style={{
                        fontWeight: "900",
                        letterSpacing: "2px",
                        fontSize: "0.9rem",
                        color: "#ffffff",
                      }}
                    >
                      STREAM
                    </span>
                    <span
                      style={{
                        fontSize: "0.65rem",
                        color: ADMIN_COLOR,
                        background: "rgba(56, 189, 248, 0.1)",
                        padding: "2px 10px",
                        borderRadius: "10px",
                        fontWeight: "bold",
                      }}
                    >
                      {globalLogs.length} logs
                    </span>
                  </div>
                  <div style={{ display: "flex", gap: "8px" }}>
                    <button
                      data-testid="admin-logs-autoscroll-btn"
                      onClick={() => setAutoScroll(!autoScroll)}
                      style={{
                        background: autoScroll ? "rgba(56, 189, 248, 0.1)" : "transparent",
                        border: `1px solid ${autoScroll ? ADMIN_COLOR : "var(--color-panel-border)"}`,
                        color: autoScroll ? ADMIN_COLOR : "var(--color-text-muted)",
                        padding: "4px 12px",
                        borderRadius: "6px",
                        cursor: "pointer",
                        fontSize: "0.65rem",
                        fontWeight: "bold",
                        letterSpacing: "0.5px",
                      }}
                    >
                      {autoScroll ? "⏬ AUTO" : "⏸ PAUSED"}
                    </button>
                    <button
                      data-testid="admin-logs-refresh-btn"
                      onClick={loadFeed}
                      style={{
                        background: "transparent",
                        border: "1px solid var(--color-panel-border)",
                        color: "var(--color-text-muted)",
                        padding: "4px 12px",
                        borderRadius: "6px",
                        cursor: "pointer",
                        fontSize: "0.65rem",
                        fontWeight: "bold",
                      }}
                    >
                      ↻ REFRESH
                    </button>
                  </div>
                </div>

                {/* Feed Content */}
                <div style={{ flex: 1, overflowY: "auto", padding: "0" }}>
                  {feedLoading ? (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "200px",
                      }}
                    >
                      <div className="loader"></div>
                    </div>
                  ) : globalLogs.length === 0 ? (
                    <div
                      style={{
                        padding: "60px 40px",
                        textAlign: "center",
                        color: "var(--color-text-muted)",
                      }}
                    >
                      <i
                        className="bx bx-broadcast"
                        style={{
                          fontSize: "3rem",
                          opacity: 0.2,
                          marginBottom: "15px",
                          display: "block",
                        }}
                      ></i>
                      <p style={{ fontSize: "1rem", marginBottom: "8px" }}>No logs available yet</p>
                      <p style={{ fontSize: "0.8rem", opacity: 0.5 }}>
                        Logs will appear here in real-time as loggers create them
                      </p>
                    </div>
                  ) : (
                    globalLogs.map((log: any) => (
                      <div
                        key={log.id}
                        style={{
                          padding: "12px 15px",
                          borderBottom: "1px solid var(--color-panel-border)",
                          fontSize: "0.8rem",
                          borderLeft:
                            (log.rating || 0) > 0 ? "4px solid #ffaa00" : "4px solid transparent",
                        }}
                      >
                        <div style={{ display: "flex", gap: "10px" }}>
                          <div style={{ minWidth: "60px" }}>
                            <div
                              style={{
                                color: "rgba(255, 255, 255, 0.8)",
                                fontFamily: "var(--font-mono)",
                                fontWeight: "normal",
                              }}
                            >
                              {log.tc}
                            </div>
                            <div
                              style={{
                                fontSize: "0.6rem",
                                color: "var(--color-accent-success)",
                                fontWeight: "900",
                                marginTop: "2px",
                              }}
                            >
                              {log.author}
                            </div>
                          </div>
                          <div style={{ flex: 1 }}>
                            <span
                              style={{ color: "var(--color-text-secondary)", lineHeight: "1.4" }}
                            >
                              {log.text &&
                                log.text.split(/(\[.*?\])/g).map((part: string, i: number) =>
                                  part.startsWith("[") && part.endsWith("]") ? (
                                    <span
                                      key={i}
                                      style={{
                                        color: "rgba(255, 255, 255, 0.8)",
                                        fontSize: "1em",
                                        fontWeight: "900",
                                        margin: "0 2px",
                                        textTransform: "uppercase",
                                      }}
                                    >
                                      {part.slice(1, -1)}
                                    </span>
                                  ) : (
                                    part
                                  ),
                                )}
                            </span>
                            <div
                              style={{
                                display: "flex",
                                gap: "4px",
                                marginTop: "6px",
                                flexWrap: "wrap",
                              }}
                            >
                              {log.cameras?.map((c: string) => {
                                const camName = getCameraName(c);
                                return camName ? (
                                  <span
                                    key={c}
                                    style={{
                                      fontSize: "0.58rem",
                                      background: "#ffaa00",
                                      color: "#000",
                                      padding: "0 6px",
                                      height: "15px",
                                      borderRadius: "20px",
                                      fontWeight: "700",
                                      textTransform: "uppercase",
                                      letterSpacing: "0.2px",
                                      display: "inline-flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      lineHeight: "1",
                                    }}
                                  >
                                    {camName}
                                  </span>
                                ) : null;
                              })}
                              {log.participants?.map((pId: string) => {
                                const p = feedParticipants.find(
                                  (p: any) => p.id?.toLowerCase() === pId.toLowerCase(),
                                );
                                if (!p) return null;
                                return (
                                  <span
                                    key={pId}
                                    style={{
                                      fontSize: "0.58rem",
                                      background: "var(--color-accent-danger)",
                                      color: "rgba(255, 255, 255, 0.8)",
                                      padding: "0 6px",
                                      height: "15px",
                                      borderRadius: "20px",
                                      fontWeight: "700",
                                      textTransform: "uppercase",
                                      letterSpacing: "0.2px",
                                      display: "inline-flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      lineHeight: "1",
                                    }}
                                  >
                                    {p.number
                                      ? `${p.number} ${p.alias || p.name}`
                                      : p.alias || p.name}
                                  </span>
                                );
                              })}
                              {log.tags?.map((t: string) => (
                                <span
                                  key={t}
                                  style={{
                                    fontSize: "0.58rem",
                                    background: getTagColor(t),
                                    color: "#000",
                                    padding: "0 6px",
                                    height: "15px",
                                    borderRadius: "20px",
                                    fontWeight: "700",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.2px",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    lineHeight: "1",
                                  }}
                                >
                                  {getTagLabel(t)}
                                </span>
                              ))}
                              {(log.rating || 0) > 0 && (
                                <div style={{ marginLeft: "4px", display: "flex" }}>
                                  <StarRating
                                    value={log.rating || 0}
                                    max={2}
                                    readOnly
                                    size="0.8rem"
                                    activeColor="#ffaa00"
                                    inactiveColor="#555"
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                  <div ref={feedEndRef} />
                </div>
              </div>
              <div style={{ flex: "0 0 450px", height: "100%" }}>
                <LiveTranscriptionsPanel
                  onSelectText={() => {}}
                  baseTc={currentDisplayTC}
                  participants={feedParticipants}
                />
              </div>
            </div>
          ) : activeView === "roles" ? (
            /* ====== ROLES VIEW ====== */
            <div data-testid="admin-view-roles" style={{ height: "100%" }}>
              <RolesEditor externalStaff={staff} activeMembers={activeLoggers} />
            </div>
          ) : activeView === "audit" ? (
            /* ====== AUDIT VIEW ====== */
            <AuditLogs />
          ) : activeView === "bugs" ? (
            /* ====== BUGS VIEW ====== */
            <BugsTable
              bugReports={bugReports}
              bugsLoading={bugsLoading}
              onRefresh={fetchBugReports}
              onResolve={(id) =>
                socketService.emit("resolve-bug-report", { id, resolvedBy: user?.name || "admin" })
              }
              onDelete={(id) => {
                setConfirmAction({
                  title: "Delete Bug Report",
                  message:
                    "Are you sure you want to delete this bug report? This action cannot be undone.",
                  onConfirm: () => {
                    socketService.emit("delete-bug-report", { id });
                    setConfirmAction(null);
                  },
                });
              }}
            />
          ) : activeView === "participants" ? (
            /* ====== PARTICIPANTS SETTINGS (from Content Director) ====== */
            <div
              data-testid="admin-view-participants"
              style={{ height: "100%", overflowY: "auto" }}
            >
              <ParticipantsEditor
                externalParticipants={feedParticipants}
                externalRoles={participantRoles}
                externalGroups={groups}
              />
            </div>
          ) : activeView === "tags" ? (
            /* ====== TAGS SETTINGS (from Content Director) ====== */
            <div data-testid="admin-view-tags" style={{ height: "100%", overflowY: "auto" }}>
              <TagsEditor />
            </div>
          ) : activeView === "rundowns" ? (
            /* ====== RUNDOWNS SETTINGS (from Content Director) ====== */
            <div data-testid="admin-view-rundowns" style={{ height: "100%", overflowY: "auto" }}>
              <RundownEditor />
            </div>
          ) : activeView === "controldash" ? (
            /* ====== CONTROL DASHBOARD (from Content Controller) ====== */
            <div data-testid="admin-view-controldash" style={{ height: "100%", overflowY: "auto" }}>
              <Dashboard
                externalActiveRundown={activeRundown}
                externalAllRundowns={rundowns}
                externalStories={feedStories}
                externalParticipants={feedParticipants}
                externalActiveLoggers={activeLoggers}
                externalStaff={staff}
                isConnected={isConnected}
                eventsCount={eventsCount}
                transcriptsCount={transcriptsCount}
                onFetchStoryLogs={handleFetchStoryLogs}
                isLoading={feedLoading}
              />
            </div>
          ) : null}
        </div>
      </div>

      {/* Create Project Modal */}
      {showNewProjectModal && (
        <div
          data-testid="admin-create-project-modal-overlay"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.8)",
            backdropFilter: "blur(15px)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
        >
          <div
            data-testid="admin-create-project-modal"
            style={{
              background: "var(--color-panel-bg)",
              border: `1px solid ${ADMIN_COLOR}`,
              borderRadius: "20px",
              padding: "30px",
              width: "min(420px, 90vw)",
              boxShadow: `0 0 50px rgba(56, 189, 248, 0.15)`,
            }}
          >
            <h2
              style={{
                color: ADMIN_COLOR,
                letterSpacing: "2px",
                textAlign: "center",
                fontSize: "1.1rem",
                margin: "0 0 25px 0",
              }}
            >
              NEW PROJECT
            </h2>
            {creationProgress.status !== "idle" && creationProgress.status !== "error" ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "20px",
                  padding: "30px 0",
                }}
              >
                <div
                  style={{
                    width: "50px",
                    height: "50px",
                    borderRadius: "50%",
                    border:
                      creationProgress.status === "ready"
                        ? "3px solid #10B981"
                        : "3px solid transparent",
                    borderTopColor: creationProgress.status === "ready" ? "#10B981" : ADMIN_COLOR,
                    animation:
                      creationProgress.status === "ready" ? "none" : "spin 1s linear infinite",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "1.5rem",
                    color: "#10B981",
                  }}
                >
                  {creationProgress.status === "ready" && <i className="bx bx-check"></i>}
                </div>
                <p
                  style={{
                    color: "var(--color-text-primary)",
                    fontSize: "1rem",
                    fontWeight: 600,
                    margin: 0,
                  }}
                >
                  {creationProgress.message}
                </p>
                <div style={{ display: "flex", gap: "8px", marginTop: "5px" }}>
                  {["creating", "creating-ai", "ready"].map((step, i) => (
                    <div
                      key={step}
                      style={{
                        width: "40px",
                        height: "4px",
                        borderRadius: "2px",
                        background:
                          ["creating", "creating-ai", "ready"].indexOf(creationProgress.status) >= i
                            ? ADMIN_COLOR
                            : "rgba(255,255,255,0.1)",
                        transition: "background 0.3s ease",
                      }}
                    />
                  ))}
                </div>
              </div>
            ) : (
              <form data-testid="admin-create-project-form" onSubmit={handleCreateProject}>
                <div style={{ marginBottom: "20px" }}>
                  <label
                    style={{
                      fontSize: "0.65rem",
                      color: "var(--color-text-muted)",
                      marginBottom: "5px",
                      display: "block",
                      fontWeight: "bold",
                      letterSpacing: "0.5px",
                    }}
                  >
                    PROJECT NAME
                  </label>
                  <input
                    type="text"
                    data-testid="admin-create-project-name-input"
                    style={{
                      background: "var(--color-bg-base)",
                      color: "#fff",
                      border: "1px solid var(--color-panel-border)",
                      padding: "12px",
                      width: "100%",
                      borderRadius: "10px",
                      fontSize: "1rem",
                      fontWeight: "bold",
                      letterSpacing: "0.5px",
                    }}
                    placeholder="e.g. MASTERCHEF CELEBRITY"
                    value={name}
                    onChange={(e) => setName(e.target.value.toUpperCase())}
                    required
                  />
                </div>
                {creationProgress.status === "error" && (
                  <div
                    style={{
                      background: "rgba(239,68,68,0.1)",
                      border: "1px solid rgba(239,68,68,0.3)",
                      borderRadius: "8px",
                      padding: "10px",
                      marginBottom: "15px",
                    }}
                  >
                    <p style={{ color: "#EF4444", fontSize: "0.85rem", margin: 0 }}>
                      {creationProgress.message}
                    </p>
                  </div>
                )}
                <div style={{ marginBottom: "25px" }}>
                  <label
                    style={{
                      fontSize: "0.65rem",
                      color: "var(--color-text-muted)",
                      marginBottom: "5px",
                      display: "block",
                      fontWeight: "bold",
                      letterSpacing: "0.5px",
                    }}
                  >
                    DESCRIPTION
                  </label>
                  <textarea
                    data-testid="admin-create-project-description-input"
                    style={{
                      background: "var(--color-bg-base)",
                      color: "#fff",
                      border: "1px solid var(--color-panel-border)",
                      padding: "12px",
                      width: "100%",
                      borderRadius: "10px",
                      minHeight: "80px",
                      resize: "vertical",
                    }}
                    placeholder="Workspace description..."
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                  />
                </div>
                <div style={{ display: "flex", justifyContent: "center", gap: "20px" }}>
                  <button
                    data-testid="admin-create-project-cancel-btn"
                    type="button"
                    onClick={() => setShowNewProjectModal(false)}
                    style={{
                      background: "transparent",
                      border: "none",
                      color: "#666",
                      cursor: "pointer",
                      fontWeight: "bold",
                      fontSize: "0.85rem",
                    }}
                  >
                    CANCEL
                  </button>
                  <button
                    data-testid="admin-create-project-submit-btn"
                    type="submit"
                    style={{
                      background: `linear-gradient(135deg, ${ADMIN_COLOR} 0%, #0EA5E9 100%)`,
                      color: "#000",
                      border: "none",
                      padding: "10px 30px",
                      borderRadius: "20px",
                      fontWeight: "800",
                      cursor: "pointer",
                      fontSize: "0.85rem",
                      letterSpacing: "1px",
                    }}
                  >
                    CREATE
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Edit Project Modal */}
      {editingProject && (
        <div
          data-testid="admin-edit-project-modal-overlay"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.8)",
            backdropFilter: "blur(15px)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
        >
          <div
            data-testid="admin-edit-project-modal"
            style={{
              background: "var(--color-panel-bg)",
              border: `1px solid #A3E635`,
              borderRadius: "20px",
              padding: "30px",
              width: "min(420px, 90vw)",
              boxShadow: `0 0 50px rgba(163, 230, 53, 0.15)`,
            }}
          >
            <h2
              style={{
                color: "#A3E635",
                letterSpacing: "2px",
                textAlign: "center",
                fontSize: "1.1rem",
                margin: "0 0 25px 0",
              }}
            >
              EDIT PROJECT
            </h2>
            <form data-testid="admin-edit-project-form" onSubmit={handleEditSubmit}>
              <div style={{ marginBottom: "20px" }}>
                <label
                  style={{
                    fontSize: "0.65rem",
                    color: "var(--color-text-muted)",
                    marginBottom: "5px",
                    display: "block",
                    fontWeight: "bold",
                    letterSpacing: "0.5px",
                  }}
                >
                  PROJECT NAME
                </label>
                <input
                  type="text"
                  data-testid="admin-edit-project-name-input"
                  style={{
                    background: "var(--color-bg-base)",
                    color: "#fff",
                    border: "1px solid var(--color-panel-border)",
                    padding: "12px",
                    width: "100%",
                    borderRadius: "10px",
                    fontSize: "1rem",
                    fontWeight: "bold",
                    letterSpacing: "0.5px",
                  }}
                  placeholder="e.g. MASTERCHEF CELEBRITY"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value.toUpperCase())}
                  required
                />
              </div>
              {editError && (
                <div
                  style={{
                    background: "rgba(239,68,68,0.1)",
                    border: "1px solid rgba(239,68,68,0.3)",
                    borderRadius: "8px",
                    padding: "10px",
                    marginBottom: "15px",
                  }}
                >
                  <p style={{ color: "#EF4444", fontSize: "0.85rem", margin: 0 }}>{editError}</p>
                </div>
              )}
              <div style={{ marginBottom: "25px" }}>
                <label
                  style={{
                    fontSize: "0.65rem",
                    color: "var(--color-text-muted)",
                    marginBottom: "5px",
                    display: "block",
                    fontWeight: "bold",
                    letterSpacing: "0.5px",
                  }}
                >
                  DESCRIPTION
                </label>
                <textarea
                  style={{
                    background: "var(--color-bg-base)",
                    color: "#fff",
                    border: "1px solid var(--color-panel-border)",
                    padding: "12px",
                    width: "100%",
                    borderRadius: "10px",
                    minHeight: "80px",
                    resize: "vertical",
                  }}
                  placeholder="Workspace description..."
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  data-testid="admin-edit-project-description-input"
                />
              </div>
              <div style={{ display: "flex", justifyContent: "center", gap: "20px" }}>
                <button
                  data-testid="admin-edit-project-cancel-btn"
                  type="button"
                  onClick={() => setEditingProject(null)}
                  style={{
                    background: "transparent",
                    border: "none",
                    color: "#666",
                    cursor: "pointer",
                    fontWeight: "bold",
                    fontSize: "0.85rem",
                  }}
                >
                  CANCEL
                </button>
                <button
                  data-testid="admin-edit-project-submit-btn"
                  type="submit"
                  disabled={isEditing}
                  style={{
                    background: `linear-gradient(135deg, #A3E635 0%, #0EA5E9 100%)`,
                    color: "#000",
                    border: "none",
                    padding: "10px 30px",
                    borderRadius: "20px",
                    fontWeight: "800",
                    cursor: isEditing ? "not-allowed" : "pointer",
                    fontSize: "0.85rem",
                    letterSpacing: "1px",
                    opacity: isEditing ? 0.7 : 1,
                  }}
                >
                  {isEditing ? "SAVING..." : "SAVE CHANGES"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Destructive Delete Confirmation Modal */}
      {deleteTarget && (
        <div
          data-testid="admin-delete-project-modal-overlay"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.85)",
            backdropFilter: "blur(20px)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1100,
          }}
        >
          <div
            data-testid="admin-delete-project-modal"
            style={{
              background: "var(--color-panel-bg)",
              border: "1px solid #EF4444",
              borderRadius: "20px",
              padding: "30px",
              width: "min(450px, 90vw)",
              boxShadow: "0 0 60px rgba(239, 68, 68, 0.2)",
            }}
          >
            {/* Warning Icon */}
            <div style={{ textAlign: "center", marginBottom: "20px" }}>
              <div
                style={{
                  width: "60px",
                  height: "60px",
                  borderRadius: "50%",
                  background: "rgba(239, 68, 68, 0.1)",
                  border: "2px solid #EF4444",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "1.8rem",
                  color: "#EF4444",
                }}
              >
                <i className="bx bx-error"></i>
              </div>
            </div>

            <h2
              style={{
                color: "#EF4444",
                letterSpacing: "2px",
                textAlign: "center",
                fontSize: "1rem",
                margin: "0 0 15px 0",
                fontWeight: "900",
              }}
            >
              DESTRUCTIVE ACTION
            </h2>

            <p
              style={{
                color: "var(--color-text-secondary)",
                fontSize: "0.85rem",
                textAlign: "center",
                lineHeight: "1.6",
                margin: "0 0 10px 0",
              }}
            >
              You are about to permanently delete the project:
            </p>

            <div
              style={{
                background: "rgba(239, 68, 68, 0.08)",
                border: "1px solid rgba(239, 68, 68, 0.2)",
                borderRadius: "10px",
                padding: "12px",
                textAlign: "center",
                marginBottom: "15px",
              }}
            >
              <span
                style={{
                  color: "#EF4444",
                  fontWeight: "900",
                  fontSize: "1.1rem",
                  letterSpacing: "1px",
                }}
              >
                {deleteTarget.name}
              </span>
            </div>

            <p
              style={{
                color: "var(--color-text-muted)",
                fontSize: "0.75rem",
                textAlign: "center",
                lineHeight: "1.6",
                margin: "0 0 20px 0",
              }}
            >
              This will deactivate the project in the database and delete its Vertex AI Data Store.
              This action{" "}
              <span style={{ color: "#EF4444", fontWeight: "bold" }}>cannot be undone</span>.
            </p>

            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  fontSize: "0.7rem",
                  color: "var(--color-text-muted)",
                  marginBottom: "8px",
                  display: "block",
                  textAlign: "center",
                }}
              >
                Type{" "}
                <span
                  style={{
                    color: "#EF4444",
                    fontWeight: "900",
                    fontFamily: "monospace",
                    background: "rgba(239, 68, 68, 0.1)",
                    padding: "2px 8px",
                    borderRadius: "4px",
                    letterSpacing: "2px",
                  }}
                >
                  DELETE
                </span>{" "}
                to confirm
              </label>
              <input
                data-testid="admin-delete-project-confirm-input"
                type="text"
                style={{
                  background: "var(--color-bg-base)",
                  color: "#fff",
                  border: `1px solid ${deleteConfirmText === "DELETE" ? "#EF4444" : "var(--color-panel-border)"}`,
                  padding: "12px",
                  width: "100%",
                  borderRadius: "10px",
                  fontSize: "1rem",
                  fontWeight: "bold",
                  letterSpacing: "3px",
                  textAlign: "center",
                  fontFamily: "monospace",
                  transition: "border-color 0.2s ease",
                }}
                placeholder="DELETE"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value.toUpperCase())}
                autoFocus
              />
            </div>

            <div style={{ display: "flex", justifyContent: "center", gap: "15px" }}>
              <button
                data-testid="admin-delete-project-cancel-btn"
                onClick={() => {
                  setDeleteTarget(null);
                  setDeleteConfirmText("");
                }}
                style={{
                  background: "transparent",
                  border: "1px solid var(--color-panel-border)",
                  color: "var(--color-text-secondary)",
                  padding: "10px 25px",
                  borderRadius: "10px",
                  cursor: "pointer",
                  fontWeight: "bold",
                  fontSize: "0.85rem",
                  transition: "all 0.2s",
                }}
              >
                CANCEL
              </button>
              <button
                data-testid="admin-delete-project-confirm-btn"
                onClick={executeDelete}
                disabled={deleteConfirmText !== "DELETE"}
                style={{
                  background:
                    deleteConfirmText === "DELETE"
                      ? "linear-gradient(135deg, #EF4444 0%, #DC2626 100%)"
                      : "rgba(239, 68, 68, 0.1)",
                  color: deleteConfirmText === "DELETE" ? "#fff" : "rgba(239, 68, 68, 0.3)",
                  border: "none",
                  padding: "10px 25px",
                  borderRadius: "10px",
                  fontWeight: "900",
                  fontSize: "0.85rem",
                  letterSpacing: "1px",
                  cursor: deleteConfirmText === "DELETE" ? "pointer" : "not-allowed",
                  transition: "all 0.3s ease",
                  boxShadow:
                    deleteConfirmText === "DELETE" ? "0 5px 20px rgba(239, 68, 68, 0.3)" : "none",
                }}
              >
                DELETE PROJECT
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Monitor Citation Popup */}
      {monitorCitPopup && (
        <div
          onClick={() => setMonitorCitPopup(null)}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 99990,
            background: "transparent",
          }}
        >
          {(() => {
            const p = monitorCitPopup;
            const isOtf = p.type === "OTF" || p.role === "otf";
            const isTrans = p.type === "TRANS" || p.role === "transcription";
            let themeColor = "#ff4d8d"; // LOG default
            let bgThemeColor = "rgba(255, 77, 141, 0.08)";
            let titleText = "LOG DE GRABACIÓN";
            let iconClass = "bx bx-file";
            if (isOtf) {
              themeColor = "#06b6d4";
              bgThemeColor = "rgba(6, 182, 212, 0.08)";
              titleText = "ENTREVISTA (OTF)";
              iconClass = "bx bx-camera-movie";
            } else if (isTrans) {
              themeColor = "#8B5CF6";
              bgThemeColor = "rgba(163, 230, 53, 0.08)";
              titleText = "TRANSCRIPCIÓN DE AUDIO";
              iconClass = "bx bx-microphone";
            }
            return (
              <div
                onClick={(e) => e.stopPropagation()}
                style={{
                  position: "fixed",
                  left: Math.min(p.x, window.innerWidth - 360),
                  top: Math.max(p.y - 180, 10),
                  width: "340px",
                  background: "rgba(25, 25, 35, 0.97)",
                  backdropFilter: "blur(16px)",
                  border: `1px solid ${themeColor}66`,
                  borderRadius: "12px",
                  padding: "16px",
                  zIndex: 99991,
                  boxShadow: `0 8px 32px rgba(0,0,0,0.6), 0 0 20px ${themeColor}22`,
                  animation: "fadeIn 0.15s ease-out",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "10px",
                  }}
                >
                  <span
                    style={{
                      fontSize: "0.72rem",
                      color: themeColor,
                      fontWeight: 700,
                      letterSpacing: "0.05em",
                    }}
                  >
                    <i className={iconClass} style={{ marginRight: "5px" }}></i> {titleText}
                  </span>
                  <span
                    onClick={() => setMonitorCitPopup(null)}
                    style={{
                      cursor: "pointer",
                      color: "#888",
                      fontSize: "1.1rem",
                      lineHeight: 1,
                      padding: "2px 4px",
                    }}
                  >
                    ×
                  </span>
                </div>
                {p.speaker && (
                  <div
                    style={{
                      fontSize: "0.82rem",
                      color: "#eee",
                      fontWeight: 600,
                      marginBottom: "6px",
                    }}
                  >
                    {p.speaker}
                  </div>
                )}
                {p.tc && (
                  <div style={{ fontSize: "0.68rem", color: "#888", marginBottom: "8px" }}>
                    <i className="bx bx-time" style={{ marginRight: "4px" }}></i>
                    {p.tc}
                  </div>
                )}
                <div
                  style={{
                    fontSize: "0.78rem",
                    color: "#ccc",
                    lineHeight: "1.5",
                    fontStyle: "italic",
                    maxHeight: "120px",
                    overflowY: "auto",
                    background: bgThemeColor,
                    borderRadius: "8px",
                    padding: "10px 12px",
                  }}
                >
                  "{p.text}"
                </div>
                <div
                  style={{
                    fontSize: "0.6rem",
                    color: "#555",
                    marginTop: "8px",
                    textAlign: "right",
                  }}
                >
                  {p.srcKey}
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* General Tooltip Popup */}
      {tooltipInfo && (
        <div
          style={{
            position: "fixed",
            left: Math.min(tooltipInfo.x, window.innerWidth - 320),
            top: Math.min(tooltipInfo.y, window.innerHeight - 80),
            width: "300px",
            background: "rgba(25, 25, 35, 0.98)",
            backdropFilter: "blur(16px)",
            border: `1px solid ${ADMIN_COLOR}`,
            boxShadow: `0 10px 40px rgba(0,0,0,0.8), 0 0 15px rgba(163, 230, 53, 0.1)`,
            borderRadius: "10px",
            padding: "12px 16px",
            zIndex: 99999,
            pointerEvents: "none",
          }}
        >
          <div
            style={{
              fontSize: "0.65rem",
              fontWeight: 800,
              color: ADMIN_COLOR,
              letterSpacing: "0.5px",
              marginBottom: "6px",
              textTransform: "uppercase",
            }}
          >
            <i className="bx bx-message-square-detail" style={{ marginRight: "6px" }}></i> FULL
            QUERY PREVIEW
          </div>
          <div
            style={{
              fontSize: "0.75rem",
              color: "#e0e0e0",
              lineHeight: 1.5,
              wordWrap: "break-word",
            }}
          >
            {tooltipInfo.text}
          </div>
        </div>
      )}

      {/* Screenshot Preview Modal */}
      {screenshotPreview && (
        <div
          onClick={() => setScreenshotPreview(null)}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: "100vw",
            height: "100vh",
            background: "rgba(0,0,0,0.85)",
            backdropFilter: "blur(12px)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 99999,
            cursor: "pointer",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ position: "relative", maxWidth: "90vw", maxHeight: "85vh" }}
          >
            <img
              src={screenshotPreview}
              alt="Bug screenshot"
              style={{
                maxWidth: "90vw",
                maxHeight: "85vh",
                borderRadius: "12px",
                border: `2px solid ${ADMIN_COLOR}`,
                boxShadow: `0 20px 60px rgba(0,0,0,0.5), 0 0 40px rgba(163, 230, 53, 0.15)`,
              }}
            />
            <button
              onClick={() => setScreenshotPreview(null)}
              style={{
                position: "absolute",
                top: "-12px",
                right: "-12px",
                background: ADMIN_COLOR,
                border: "none",
                color: "#fff",
                width: "28px",
                height: "28px",
                borderRadius: "50%",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "1rem",
                boxShadow: "0 4px 12px rgba(0,0,0,0.4)",
              }}
            >
              <i className="bx bx-x"></i>
            </button>
          </div>
        </div>
      )}

      {/* Confirm Action Modal */}
      {confirmAction && (
        <div
          onClick={() => setConfirmAction(null)}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            background: "rgba(0,0,0,0.7)",
            backdropFilter: "blur(8px)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 99999,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#1a1a2e",
              border: `1px solid rgba(239, 68, 68, 0.3)`,
              borderRadius: "16px",
              padding: "30px",
              maxWidth: "420px",
              width: "90%",
              boxShadow: "0 20px 60px rgba(0,0,0,0.5), 0 0 40px rgba(239, 68, 68, 0.1)",
            }}
          >
            <div
              style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "16px" }}
            >
              <i
                className="bx bx-error-circle"
                style={{ fontSize: "1.5rem", color: "#EF4444" }}
              ></i>
              <div style={{ fontSize: "1rem", fontWeight: 800, color: "#fff" }}>
                {confirmAction.title}
              </div>
            </div>
            <div
              style={{ fontSize: "0.8rem", color: "#999", lineHeight: 1.5, marginBottom: "24px" }}
            >
              {confirmAction.message}
            </div>
            <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
              <button
                onClick={() => setConfirmAction(null)}
                style={{
                  background: "rgba(255,255,255,0.05)",
                  border: "1px solid rgba(255,255,255,0.1)",
                  color: "#999",
                  padding: "8px 20px",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "0.75rem",
                  fontWeight: 700,
                }}
              >
                CANCEL
              </button>
              <button
                onClick={confirmAction.onConfirm}
                style={{
                  background: "rgba(239, 68, 68, 0.15)",
                  border: "1px solid rgba(239, 68, 68, 0.4)",
                  color: "#EF4444",
                  padding: "8px 20px",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "0.75rem",
                  fontWeight: 800,
                }}
              >
                CONFIRM
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
